import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useApiConfig } from '@/app/ApiProvider';
import { apiFetch } from '@/app/api-fetch';
import { orgRoute } from '@/app/org-route';
import { FileToDataURL } from '@/lib/FileToDataURL';

// SSiteNew — standalone site creation (plant + floorplan). The
// onboarding wizard covers first-site setup; this screen is for
// returning admins to add additional plants.
export function SSiteNew() {
  const apiConfig = useApiConfig();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [floorplan, setFloorplan] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(apiConfig, '/v1/sites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), floorplan_url: floorplan || undefined }),
      });
      if (!res.ok) throw new Error('failed to create site');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      navigate({ to: orgRoute('/fleet') });
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'failed'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError('Site name is required.');
      return;
    }
    create.mutate();
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-sp-6 px-sp-6 py-sp-9">
      <header className="flex flex-col gap-sp-2">
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Fleet
        </p>
        <h1 className="text-fs-h1 font-medium text-gray-900">Add site</h1>
      </header>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-sp-5 rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      >
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Site name
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Plant A"
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
          />
        </label>
        <FileToDataURL
          label="Floorplan (optional, ≤ 1 MiB)"
          accept="image/png,image/jpeg,image/svg+xml"
          value={floorplan}
          onChange={setFloorplan}
        />
        {formError ? (
          <p role="alert" className="text-fs-meta text-critical">{formError}</p>
        ) : null}
        <div className="flex items-center gap-sp-3">
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-r-default bg-info px-sp-4 py-sp-2 text-fs-body font-medium text-gray-50 disabled:opacity-60"
          >
            {create.isPending ? 'Saving…' : 'Add site'}
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: orgRoute('/fleet') })}
            className="rounded-r-default border border-gray-300 px-sp-4 py-sp-2 text-fs-body text-gray-900"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
